import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, MessageCircle, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NAV, PHONE, PHONE_TEL, WHATSAPP, WHATSAPP_MSG } from "@/data/site";
import { track } from "@/lib/analytics";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const isActive = (p) => loc.pathname === p || (p !== "/" && loc.pathname.startsWith(p));

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-slate-200" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 lg:h-20 gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link" onClick={() => setOpen(false)}>
            <span className="text-2xl lg:text-3xl font-extrabold tracking-tight" style={{ color: "var(--sc-emerald)", fontFamily: "Outfit" }}>
              SENIOR<span style={{ color: "var(--sc-emerald)" }}>CAR</span><sup className="text-[0.5em] align-super font-bold -ml-0.5">®</sup>
            </span>
          </Link>

          <nav className="hidden xl:flex items-center gap-1" data-testid="desktop-nav">
            {NAV.map((n) => n.children ? (
              <div key={n.path} className="relative group">
                <Link to={n.path}
                  className={`flex items-center gap-1 px-3 py-2 text-sm font-semibold rounded-md transition-colors ${isActive(n.path) ? "text-white" : "text-slate-700 hover:text-emerald-900 hover:bg-emerald-50"}`}
                  style={isActive(n.path) ? { backgroundColor: "var(--sc-emerald)" } : {}}
                  data-testid={`nav-${n.path.replace(/\//g, "-").replace(/^-/, "")}`}>
                  {n.label} <ChevronDown size={15} className="transition-transform group-hover:rotate-180" />
                </Link>
                <div className="absolute left-0 top-full pt-2 hidden group-hover:block min-w-[210px] z-50">
                  <div className="rounded-xl border border-slate-200 bg-white shadow-xl py-2" data-testid={`nav-dropdown-${n.path.replace(/\//g, "-").replace(/^-/, "")}`}>
                    {n.children.map((c) => (
                      <Link key={c.path} to={c.path}
                        className="block px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-emerald-50 hover:text-emerald-900 transition-colors"
                        data-testid={`nav-sub-${c.path.replace(/\//g, "-").replace(/^-/, "")}`}>
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link key={n.path} to={n.path}
                className={`px-3 py-2 text-sm font-semibold rounded-md transition-colors ${isActive(n.path) ? "text-white" : "text-slate-700 hover:text-emerald-900 hover:bg-emerald-50"}`}
                style={isActive(n.path) ? { backgroundColor: "var(--sc-emerald)" } : {}}
                data-testid={`nav-${n.path.replace(/\//g, "-").replace(/^-/, "")}`}>
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a href={`tel:${PHONE_TEL}`} className="hidden md:flex items-center gap-2 text-emerald-900 font-semibold text-sm px-3 py-2 rounded-md hover:bg-emerald-50 whitespace-nowrap shrink-0"
              data-testid="header-phone" onClick={() => track("phone_click", { source: "header" })}>
              <Phone size={18} className="shrink-0" /> {PHONE}
            </a>
            <Link to="/offerta" className="hidden sm:block">
              <Button className="text-white font-bold h-11 px-5 rounded-full" style={{ backgroundColor: "var(--sc-amber)" }}
                data-testid="header-prenota-prova-button" onClick={() => track("cta_click", { cta: "ordina_online", source: "header" })}>
                Prenota Online
              </Button>
            </Link>
            <button className="xl:hidden p-2 text-slate-800" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
              {open ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="xl:hidden border-t border-slate-200 bg-white" data-testid="mobile-nav">
          <div className="px-4 py-3 flex flex-col">
            {NAV.map((n) => (
              <React.Fragment key={n.path}>
                <Link to={n.path} onClick={() => setOpen(false)}
                  className="py-3 text-lg font-semibold text-slate-800 border-b border-slate-100"
                  data-testid={`mobile-nav-${n.path.replace(/\//g, "-").replace(/^-/, "")}`}>
                  {n.label}
                </Link>
                {n.children?.map((c) => (
                  <Link key={c.path} to={c.path} onClick={() => setOpen(false)}
                    className="py-3 pl-6 text-base font-semibold text-emerald-800 border-b border-slate-100 flex items-center gap-2"
                    data-testid={`mobile-nav-sub-${c.path.replace(/\//g, "-").replace(/^-/, "")}`}>
                    <ChevronDown size={16} className="-rotate-90" /> {c.label}
                  </Link>
                ))}
              </React.Fragment>
            ))}
            <div className="flex gap-2 mt-4">
              <a href={`tel:${PHONE_TEL}`} className="flex-1" onClick={() => track("phone_click", { source: "mobile_menu" })}>
                <Button variant="outline" className="w-full h-12 gap-2 font-semibold"><Phone size={18} /> Chiama</Button>
              </a>
              <a href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(WHATSAPP_MSG)}`} target="_blank" rel="noreferrer" className="flex-1"
                onClick={() => track("whatsapp_click", { source: "mobile_menu" })}>
                <Button className="w-full h-12 gap-2 font-semibold bg-[#25D366] hover:bg-[#1eb658] text-white"><MessageCircle size={18} /> WhatsApp</Button>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
